import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, Camera, FileText, Languages, Award, Save, 
  ArrowLeft, Check, X, Plus, HeartHandshake, Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { workersAPI, filesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const WorkerProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const [formData, setFormData] = useState({
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    bio: '',
    certifications: [],
    languages: [],
    profile_completed: false
  });
  
  const [newCert, setNewCert] = useState('');
  const [newLang, setNewLang] = useState('');

  useEffect(() => {
    fetchProfile();
     
  }, []); // Intentionally run only on mount

  const fetchProfile = async () => {
    try {
      const response = await workersAPI.getMyProfile();
      setProfile(response.data);
      setFormData({
        phone: response.data.phone || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        bio: response.data.bio || '',
        certifications: response.data.certifications || [],
        languages: response.data.languages || [],
        profile_completed: response.data.profile_completed || false
      });
      if (response.data.profile_picture) {
        setPreviewUrl(filesAPI.getUrl(response.data.profile_picture));
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCertification = () => {
    if (newCert.trim() && !formData.certifications.includes(newCert.trim())) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCert.trim()]
      }));
      setNewCert('');
    }
  };

  const handleRemoveCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(c => c !== cert)
    }));
  };

  const handleAddLanguage = () => {
    if (newLang.trim() && !formData.languages.includes(newLang.trim())) {
      setFormData(prev => ({
        ...prev,
        languages: [...prev.languages, newLang.trim()]
      }));
      setNewLang('');
    }
  };

  const handleRemoveLanguage = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter(l => l !== lang)
    }));
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await workersAPI.uploadPicture(formDataUpload);
      toast.success('Profile picture uploaded!');
      setPreviewUrl(filesAPI.getUrl(response.data.path));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setPreviewUrl(profile?.profile_picture ? filesAPI.getUrl(profile.profile_picture) : null);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (markComplete = false) => {
    setSaving(true);
    try {
      const updateData = {
        ...formData,
        profile_completed: markComplete || formData.profile_completed
      };
      
      await workersAPI.updateMyProfile(updateData);
      toast.success(markComplete ? 'Profile completed!' : 'Profile saved!');
      
      if (markComplete) {
        navigate('/worker/dashboard');
      } else {
        fetchProfile();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="animate-pulse-soft text-[var(--primary)]">Loading...</div>
      </div>
    );
  }

  const isProfileComplete = formData.phone && formData.address && formData.city && formData.state && formData.bio;

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <header className="nav-glass sticky top-0 z-40 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/worker/dashboard" className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary)]">
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <HeartHandshake className="w-6 h-6 text-[var(--primary)]" />
            <span className="font-bold gradient-text">NC Help Hub</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-['Cormorant_Garamond']">Complete Your Profile</h1>
          <p className="text-[var(--text-secondary)] mt-1">
            Add your information so customers can learn more about you
          </p>
        </div>

        {/* Verification Badge */}
        {profile?.is_verified && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-[var(--success)]/10 border border-[var(--success)]/20 rounded-xl flex items-center gap-3"
          >
            <div className="w-10 h-10 bg-[var(--success)] rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-[var(--success)]">Verified Worker</p>
              <p className="text-sm text-[var(--text-muted)]">
                Your profile is verified and customers can see your verified badge
              </p>
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Picture Section */}
          <div className="lg:col-span-1">
            <div className="card p-6 text-center">
              <h2 className="font-semibold mb-4 flex items-center justify-center gap-2">
                <Camera className="w-5 h-5 text-[var(--primary)]" />
                Profile Photo
              </h2>
              
              <div className="relative inline-block">
                <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-4 border-[var(--accent)] bg-[var(--background-subtle)]">
                  {previewUrl ? (
                    <img 
                      src={previewUrl} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-16 h-16 text-[var(--text-muted)]" />
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute bottom-2 right-2 w-10 h-10 bg-[var(--primary)] text-white rounded-full flex items-center justify-center hover:bg-[var(--primary-hover)] transition-all shadow-lg"
                  data-testid="upload-photo-btn"
                >
                  {uploading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-5 h-5" />
                  )}
                </button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>
              
              <p className="text-xs text-[var(--text-muted)] mt-4">
                Upload a professional photo. Max 5MB.
              </p>

              {/* Stats */}
              <div className="mt-6 pt-6 border-t border-[var(--border-primary)]">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-[var(--warning)]">{profile?.rating || 5.0} ★</p>
                    <p className="text-xs text-[var(--text-muted)]">Rating</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[var(--primary)]">{profile?.total_jobs || 0}</p>
                    <p className="text-xs text-[var(--text-muted)]">Jobs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="card p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[var(--primary)]" />
                Personal Information
              </h2>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={profile?.full_name || ''}
                      disabled
                      className="input-styled mt-1 bg-[var(--background-subtle)]"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={profile?.email || ''}
                      disabled
                      className="input-styled mt-1 bg-[var(--background-subtle)]"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-styled mt-1"
                    placeholder="(555) 123-4567"
                    data-testid="profile-phone"
                  />
                </div>
                
                <div>
                  <Label>Street Address *</Label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="input-styled mt-1"
                    placeholder="123 Main St"
                    data-testid="profile-address"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="input-styled mt-1"
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <Input
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="input-styled mt-1"
                    />
                  </div>
                  <div>
                    <Label>ZIP Code</Label>
                    <Input
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      className="input-styled mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="card p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[var(--primary)]" />
                About Me
              </h2>
              
              <div>
                <Label>Bio *</Label>
                <Textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  className="input-styled mt-1"
                  rows={4}
                  placeholder="Tell customers about yourself, your experience, and why you love helping people..."
                  data-testid="profile-bio"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  This will be visible to customers when they view your profile
                </p>
              </div>
            </div>

            {/* Certifications */}
            <div className="card p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-[var(--primary)]" />
                Certifications & Credentials
              </h2>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.certifications.map((cert) => (
                  <span
                    key={`cert-${cert}`}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--accent)] text-[var(--primary)] rounded-full text-sm"
                  >
                    <Award className="w-3 h-3" />
                    {cert}
                    <button
                      onClick={() => handleRemoveCertification(cert)}
                      className="ml-1 hover:text-[var(--error)]"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  value={newCert}
                  onChange={(e) => setNewCert(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCertification())}
                  className="input-styled flex-1"
                  placeholder="Add certification (e.g., CPR Certified, First Aid)"
                />
                <Button 
                  type="button" 
                  onClick={handleAddCertification}
                  className="btn-secondary"
                >
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Languages */}
            <div className="card p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <Languages className="w-5 h-5 text-[var(--primary)]" />
                Languages Spoken
              </h2>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.languages.map((lang) => (
                  <span
                    key={`lang-${lang}`}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--secondary)]/10 text-[var(--secondary)] rounded-full text-sm"
                  >
                    {lang}
                    <button
                      onClick={() => handleRemoveLanguage(lang)}
                      className="ml-1 hover:text-[var(--error)]"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  value={newLang}
                  onChange={(e) => setNewLang(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLanguage())}
                  className="input-styled flex-1"
                  placeholder="Add language (e.g., English, Spanish)"
                />
                <Button 
                  type="button" 
                  onClick={handleAddLanguage}
                  className="btn-secondary"
                >
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Save Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="btn-secondary flex-1"
                data-testid="save-profile-btn"
              >
                <Save className="w-5 h-5 mr-2" />
                Save Draft
              </Button>
              <Button
                onClick={() => handleSave(true)}
                disabled={saving || !isProfileComplete}
                className="btn-primary flex-1"
                data-testid="complete-profile-btn"
              >
                {saving ? 'Saving...' : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Complete Profile
                  </>
                )}
              </Button>
            </div>
            
            {!isProfileComplete && (
              <p className="text-sm text-[var(--warning)] text-center">
                Please fill in all required fields (*) to complete your profile
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkerProfile;
